import {
    call,
    delay,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import get from 'lodash.get';
import fileDownload from 'js-file-download';
import moment from 'moment';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    REPORTS_HARVEST_INSPECTIONS_ALL_LOAD_REQUESTED,
    REPORTS_HARVEST_INSPECTIONS_ALL_POLLING_REQUESTED,
    REPORTS_HARVEST_INSPECTIONS_ALL_POLLING_CANCELED,
    REPORTS_HARVEST_INSPECTIONS_ALL_EXPORT_REQUESTED
} from './constants';

// Actions
import setHash from '../hashes';
import { enqueueSnackbar } from '../notifications/actions';
import {
    succeededLoadReportsHarvestInspectionsAll,
    failedLoadReportsHarvestInspectionsAll,
    succeededPollingReportsHarvestInspectionsAll,
    failedPollingReportsHarvestInspectionsAll,
    succeededExportReportsHarvestInspectionsAll,
    failedExportReportsHarvestInspectionsAll,
    setReportsHarvestInspectionsAll
} from './actions';

// Selectors
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import { selectCurrentSeasonId } from '../currentSeasonId/selectors';
import { selectCurrentEntityId } from '../currentEntityId/selectors';
import { makeSelectHash } from '../hashes/selectors';
import { selectIsOnline } from '../connection/selectors';

// Utils
import axiosRetry from '../../utils/axiosRetry';

// Environment
const REACT_APP_API_URL: string = String(process.env.REACT_APP_API_URL);

// Constants
const namespace = 'reportsHarvestInspectionsAll';

type Action = {
    +type: string,
    +payload: Object
};

type Config = ?{
    headers?: Object,
    params?: Object
};

export function* getReportsHarvestInspectionsAll(config: Config): Saga<void> {
    const organizationId = yield select(selectCurrentOrganizationId);
    const seasonId = yield select(selectCurrentSeasonId);
    const entityId = yield select(selectCurrentEntityId);

    const url = `${REACT_APP_API_URL}/v1/Reports/harvestInspections/all`;

    return yield call(axiosRetry, {
        method: 'GET',
        url,
        ...(config || {}),
        params: {
            ...(config && config.params ? config.params : {}),
            organizationId,
            filter: {
                require: {
                    $and: [
                        {
                            'event.entity.seasonId': seasonId,
                            // TODO Filter out archived entities from reporting
                            // 'event.entity.archivedAt': { $exists: false },
                            ...(entityId && {
                                $or: [
                                    {
                                        'event.entity.path': {
                                            $like: `/${entityId}/%`
                                        }
                                    },
                                    {
                                        'event.entityId': entityId
                                    }
                                ]
                            })
                        }
                    ]
                }
            }
        }
    });
}

export function* loadReportsHarvestInspectionsAll(action: Action): Saga<void> {
    try {
        const response = yield call(getReportsHarvestInspectionsAll);

        const hash = get(response, 'headers.x-hash');

        // Succeeded Load Reports Harvest Inspections
        yield put(succeededLoadReportsHarvestInspectionsAll(response));

        // Set Reports Harvest Inspections
        yield put(setReportsHarvestInspectionsAll(response.data.data));

        // Set Hash
        yield put(setHash(namespace, hash));
    } catch (error) {
        yield put(failedLoadReportsHarvestInspectionsAll(error));
    }
}

export function* watchReportsHarvestInspectionsAllLoadRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_INSPECTIONS_ALL_LOAD_REQUESTED,
        loadReportsHarvestInspectionsAll
    );
}

export function* pollReportsHarvestInspectionsAll(action: Action): Saga<void> {
    while (true) {
        yield delay(60000);

        // Select Is Online
        const isOnline = yield select(selectIsOnline);

        // Select Hash
        const hash = yield select(makeSelectHash(namespace));
        let hashMatch = false;

        if (isOnline) {
            try {
                if (hash) {
                    const hashResponse = yield call(
                        getReportsHarvestInspectionsAll,
                        {
                            params: { hash: true }
                        }
                    );

                    hashMatch = hash === get(hashResponse, 'headers.x-hash');
                }
                if (!hashMatch) {
                    const response = yield call(
                        getReportsHarvestInspectionsAll
                    );

                    const nextHash = get(response, 'headers.x-hash');

                    // Succeeded Polling Reports Harvest Inspections
                    yield put(
                        succeededPollingReportsHarvestInspectionsAll(response)
                    );

                    // Set Reports Harvest Inspections
                    yield put(
                        setReportsHarvestInspectionsAll(response.data.data)
                    );

                    // Set Hash
                    yield put(setHash(namespace, nextHash));
                }
            } catch (error) {
                // Failed Polling Reports Harvest Inspections
                yield put(failedPollingReportsHarvestInspectionsAll(error));
            }
        }
    }
}

export function* watchReportsHarvestInspectionsAllPollingRequested(): Saga<void> {
    while (true) {
        const action = yield take(
            REPORTS_HARVEST_INSPECTIONS_ALL_POLLING_REQUESTED
        );
        yield race([
            call(pollReportsHarvestInspectionsAll, action),
            take(REPORTS_HARVEST_INSPECTIONS_ALL_POLLING_CANCELED)
        ]);
    }
}

export function* exportReportsHarvestInspectionsAll(): Saga<void> {
    try {
        const response = yield call(getReportsHarvestInspectionsAll, {
            headers: {
                'Content-Type': 'text/csv; charset=utf-8',
                Accept: 'text/csv'
            }
        });

        const time = moment.utc().format();

        // File Download
        fileDownload(
            response.data,
            `reports_harvest_inspections_all_${time}.csv`
        );

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Export successful',
                options: { variant: 'success' }
            })
        );

        // Succeeded Export Reports Harvest Inspections
        yield put(succeededExportReportsHarvestInspectionsAll(response));
    } catch (error) {
        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Enqueue Snackbar
        yield put(enqueueSnackbar({ message, options: { variant: 'error' } }));

        // Failed Export Reports Harvest Inspections
        yield put(failedExportReportsHarvestInspectionsAll(error));
    }
}

export function* watchReportsHarvestInspectionsAllExportRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_INSPECTIONS_ALL_EXPORT_REQUESTED,
        exportReportsHarvestInspectionsAll
    );
}
